/**
 * Geolocation Selectors
 *
 * GUARDRAIL: Selectors gate on AUTHORITY, not STATUS
 *
 * Never expose raw position to consumers - always gate on authority.
 * This prevents future devs from doing:
 *   if (status === "watching") use position
 * which would reintroduce the bug this design exists to prevent.
 *
 * status === "watching" does NOT imply authority === "live"
 * During acquisition, you're "watching" with authority === "none"
 */

import type {
  GeoAuthority,
  GeoError,
  GeoPermission,
  GeoState,
  UserPosition,
} from "@/types/geolocation"
import { createSelector } from "@reduxjs/toolkit"

import type { RootState } from "./redux-store"

/**
 * Base selector for geolocation state
 */
export const selectGeoState = (state: RootState): GeoState => state.geolocation

/**
 * Returns position ONLY if authority is "live" (active GPS fix).
 * Use this for API requests that affect business logic (search, ranking, etc.)
 *
 * This is the PRIMARY selector for position - use this by default.
 */
export const selectLivePosition = createSelector(
  [selectGeoState],
  (geo): UserPosition | undefined => {
    return geo.authority === "live" ? (geo.position ?? undefined) : undefined
  }
)

/**
 * Returns position for display purposes (map centering, UI).
 * Includes restored/paused positions.
 *
 * NEVER use this for API requests - use selectLivePosition instead.
 */
export const selectDisplayPosition = createSelector(
  [selectGeoState],
  (geo): UserPosition | undefined => {
    return geo.position ?? undefined
  }
)

/**
 * Returns authority level for UI messaging.
 * Use to show "approximate location" vs "current location" labels.
 *
 * - "none": No position known
 * - "restored": From storage, show "approximate location"
 * - "paused": Was live, show "last known location"
 * - "live": Active GPS, show "current location"
 */
export const selectGeoAuthority = createSelector(
  [selectGeoState],
  (geo): GeoAuthority => geo.authority
)

/**
 * Returns status for loading states.
 *
 * - "idle": No geo activity
 * - "restored": Loaded from cache
 * - "paused": Was watching, tab hidden
 * - "acquiring": Watch started, waiting for first fix (show spinner)
 * - "watching": Watch active, has fix
 * - "error": Something failed
 */
export const selectGeoStatus = createSelector(
  [selectGeoState],
  (geo): GeoState["status"] => geo.status
)

/**
 * Returns permission state for UI decisions.
 */
export const selectGeoPermission = createSelector(
  [selectGeoState],
  (geo): GeoPermission => geo.permission
)

/**
 * Returns error for error UI.
 */
export const selectGeoError = createSelector(
  [selectGeoState],
  (geo): GeoError | null => geo.error
)

/**
 * Returns whether position is currently live (authority === "live").
 * Use for conditional rendering or feature gating.
 */
export const selectIsGeoLive = createSelector(
  [selectGeoState],
  (geo): boolean => geo.authority === "live"
)

/**
 * Returns whether we're currently acquiring a fix (show loading state).
 */
export const selectIsGeoAcquiring = createSelector(
  [selectGeoState],
  (geo): boolean => geo.status === "acquiring"
)

/**
 * Returns whether we're in an error state.
 */
export const selectIsGeoError = createSelector(
  [selectGeoState],
  (geo): boolean => geo.status === "error"
)

/**
 * Returns whether location permission is denied.
 */
export const selectIsGeoPermissionDenied = createSelector(
  [selectGeoState],
  (geo): boolean => geo.permission === "denied"
)

// DO NOT EXPORT: selectGeoPosition (raw position without authority check)
// If you need position, use selectLivePosition or selectDisplayPosition
